import React from 'react';
import { X, GitBranch, ArrowRight, ArrowLeft, Layers, Hash, AlertTriangle } from 'lucide-react';
import { LdgNodeData } from './useLdgGraphData';
interface NodeDetailPanelProps {
  nodeData: LdgNodeData | null;
  onClose: () => void;
  onSelectNode: (nodeId: string) => void;
}
export function NodeDetailPanel({
  nodeData,
  onClose,
  onSelectNode
}: NodeDetailPanelProps) {
  if (!nodeData) return null;
  const {
    colorSet
  } = nodeData;
  return <>
      {/* Mobile: Bottom sheet */}
      <div className="md:hidden absolute inset-x-0 bottom-0 max-h-[60vh] bg-white rounded-t-2xl shadow-2xl border-t border-slate-200 flex flex-col z-50 overflow-hidden" data-id="element-6029" data-mp="4z1gxjwrq319">
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-1 flex-shrink-0" data-id="element-6030" data-mp="3sb2fasf1c3uf">
          <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-6031" data-mp="3s97v5gbl8fre" />
        </div>

        {/* Header */}
        <div className={`px-4 py-3 border-b border-slate-100 flex items-start justify-between ${colorSet.light}`} data-id="element-6032" data-mp="3ideizy1s6oa5">
          
          <div className="min-w-0 flex-1" data-id="element-6033" data-mp="r7hh70cfsdz9">
            <div className="flex items-center gap-2 mb-0.5" data-id="element-6034" data-mp="r665mecr21xl">
              <div className={`w-2 h-2 rounded-full ${colorSet.bg}`} data-id="element-6035" data-mp="m9pgbgttdhix" />
              <span className={`text-[10px] font-bold uppercase tracking-wider ${colorSet.text}`} data-id="element-6036" data-mp="108p8uzkg6gkd">
                
                {nodeData.module}
              </span>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${colorSet.bg} text-white`} data-id="element-6037" data-mp="w4zji7vy7uya">
                
                {nodeData.levelCode}
              </span>
            </div>
            <h3 className="font-mono font-bold text-slate-900 text-base leading-tight truncate" data-id="element-6038" data-mp="3k9cajix76xr">
              {nodeData.label}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white/50 rounded-lg transition-colors flex-shrink-0 ml-2" data-id="element-6039" data-mp="ciuwsk7ycw16">
            
            <X className="w-5 h-5" data-id="element-6040" data-mp="3gxkv45wrjzdu" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-6041" data-mp="1jwoqli00sa77">
          {!nodeData.hasContent && <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2" data-id="element-6042" data-mp="1juufswigpygu">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-6043" data-mp="1tmi2kk42qrbz" />
              <p className="text-xs text-amber-700" data-id="element-6044" data-mp="1touhspig6nlc">
                Geen content — phantom node.
              </p>
            </div>}

          {/* Compact properties */}
          <div className="flex items-center gap-3 text-xs" data-id="element-6045" data-mp="10zdsi0q4jmtc">
            <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100" data-id="element-6046" data-mp="15we1ajecz3o5">
              <Layers className="w-3 h-3 text-slate-400" data-id="element-6047" data-mp="1avqudlvtp6z6" />
              <span className="font-bold text-slate-700" data-id="element-6048" data-mp="1ayd15jaw3igc">
                Tier {nodeData.tier}
              </span>
            </div>
            <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100" data-id="element-6049" data-mp="2mbys9a0b7u4i">
              <Hash className="w-3 h-3 text-slate-400" data-id="element-6050" data-mp="2r978v65t7q3n" />
              <span className="text-slate-600 truncate max-w-[120px]" data-id="element-6051" data-mp="fmx1n8n9490b">
                {nodeData.skill}
              </span>
            </div>
          </div>

          {/* Prerequisites */}
          {nodeData.prerequisites.length > 0 && <div data-id="element-6052" data-mp="5wbuuxxfby6b">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-6053" data-mp="5vso8dd84w7l">
                <ArrowRight className="w-3 h-3" data-id="element-6054" data-mp="vwl0gccp8t3" />
                Vereist ({nodeData.prerequisites.length})
              </h4>
              <div className="flex flex-wrap gap-1.5" data-id="element-6055" data-mp="3o2qbj4n2um8e">
                {nodeData.prerequisites.map(prereq => <button key={prereq} onClick={() => onSelectNode(prereq)} className="font-mono text-[11px] text-slate-600 bg-slate-50 px-2 py-1 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors" data-id="element-6056" data-mp="3j3m0s9ki8gfy">
                
                    {prereq}
                  </button>)}
              </div>
            </div>}

          {/* Dependents */}
          {nodeData.dependents.length > 0 && <div data-id="element-6057" data-mp="9z87ksaeou0r">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-6058" data-mp="a0t2ij0muez5">
                <GitBranch className="w-3 h-3" data-id="element-6059" data-mp="e0thc4rn8s6q" />
                Ontsluit ({nodeData.dependents.length})
              </h4>
              <div className="flex flex-wrap gap-1.5" data-id="element-6060" data-mp="3y4e189ugle">
                {nodeData.dependents.map(dep => <button key={dep} onClick={() => onSelectNode(dep)} className="font-mono text-[11px] text-slate-600 bg-slate-50 px-2 py-1 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors" data-id="element-6061" data-mp="52rjtz74cho7">
                
                    {dep}
                  </button>)}
              </div>
            </div>}

          {nodeData.prerequisites.length === 0 && nodeData.dependents.length === 0 && <p className="text-xs text-slate-500 italic" data-id="element-6062" data-mp="33iamcr7lchp1">
                Geïsoleerde node (geen verbindingen).
              </p>}
        </div>
      </div>

      {/* Desktop: Right side panel */}
      <div className="hidden md:flex absolute top-4 right-4 bottom-4 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 flex-col z-50 overflow-hidden" data-id="element-6063" data-mp="38j81sw17uxmu">
        {/* Header */}
        <div className={`p-4 border-b border-slate-100 flex items-start justify-between ${colorSet.light}`} data-id="element-6064" data-mp="2kw34o1cwk7ji">
          
          <div data-id="element-6065" data-mp="2tuok3nn2v6g2">
            <div className="flex items-center gap-2 mb-1" data-id="element-6066" data-mp="2tu5cp0v200ga">
              <div className={`w-2.5 h-2.5 rounded-full ${colorSet.bg}`} data-id="element-6067" data-mp="2twriauydw5vv" />
              <span className={`text-xs font-bold uppercase tracking-wider ${colorSet.text}`} data-id="element-6068" data-mp="37t4xtyjcxx7p">
                
                {nodeData.module}
              </span>
            </div>
            <h3 className="font-mono font-bold text-slate-900 text-lg leading-tight" data-id="element-6069" data-mp="3hj7se364nj85">
              {nodeData.label}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white/50 rounded-lg transition-colors" data-id="element-6070" data-mp="3cnipbpuwfk73">
            
            <X className="w-5 h-5" data-id="element-6071" data-mp="3mkeylxsmgacp" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6" data-id="element-6072" data-mp="3tnli6pviowmn">
          {!nodeData.hasContent && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2.5" data-id="element-6073" data-mp="2fhf5rqu9ztn">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-6074" data-mp="7f2gu9xe8ma2" />
              <div data-id="element-6075" data-mp="c6khewvi8kui">
                <h4 className="text-sm font-bold text-amber-800" data-id="element-6076" data-mp="c73v21e4gn2q">
                  Geen content
                </h4>
                <p className="text-xs text-amber-700 mt-0.5" data-id="element-6077" data-mp="h4m0viwuem6i">
                  Deze family heeft nog geen vragen in de database. Het is een
                  "phantom node" in de graaf.
                </p>
              </div>
            </div>}

          {/* Properties */}
          <div className="grid grid-cols-2 gap-3" data-id="element-6078" data-mp="v3vdk8luay5q">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-6079" data-mp="v2k906kklmdk">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5" data-id="element-6080" data-mp="2rz17urkgv6di">
                <Layers className="w-3 h-3" data-id="element-6081" data-mp="2pcs79h072oj9" />
                Tier
              </div>
              <div className="font-bold text-slate-900 text-lg" data-id="element-6082" data-mp="2peleoyimtcsb">
                {nodeData.tier}
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-6083" data-mp="2fmyx8kfpahh4">
                Niveau {nodeData.levelCode}
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-6084" data-mp="2ap76vuupf3f1">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5" data-id="element-6085" data-mp="38al90fz30sf2">
                <Hash className="w-3 h-3" data-id="element-6086" data-mp="33d30ursw2h84" />
                Skill
              </div>
              <div className="font-medium text-slate-900 text-sm truncate" title={nodeData.skill} data-id="element-6087" data-mp="2yc5fk6cl172e">
                
                {nodeData.skill}
              </div>
              <div className="text-xs text-slate-500 truncate" title={nodeData.subSkill} data-id="element-6088" data-mp="1mytajs536ien">
                
                {nodeData.subSkill}
              </div>
            </div>
          </div>

          {/* Prerequisites */}
          <div data-id="element-6089" data-mp="27mkrdy7ik172">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-6090" data-mp="27m17ibyaz2qe">
              <ArrowRight className="w-4 h-4 text-slate-300" data-id="element-6091" data-mp="2ckckgvjw9tcu" />
              Vereist ({nodeData.prerequisites.length})
            </h4>
            {nodeData.prerequisites.length === 0 ? <div className="text-sm text-slate-500 italic bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-6092" data-mp="22orkt42at569">
                Dit is een root node (geen vereisten).
              </div> : <div className="space-y-2" data-id="element-6093" data-mp="2vbvj6q6vyl92">
                {nodeData.prerequisites.map(prereq => <button key={prereq} onClick={() => onSelectNode(prereq)} className="w-full text-left p-2.5 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors group flex items-center justify-between" data-id="element-6094" data-mp="2hm1uq8zam2zv">
                
                    <span className="font-mono text-sm text-slate-700 group-hover:text-indigo-700 transition-colors truncate" data-id="element-6095" data-mp="39av87yyb925r">
                      {prereq}
                    </span>
                    <ArrowLeft className="w-4 h-4 text-slate-300 group-hover:text-indigo-400" data-id="element-6096" data-mp="12mmkuol05oey" />
                  </button>)}
              </div>}
          </div>

          {/* Dependents */}
          <div data-id="element-6097" data-mp="1cdpjzkudftbl">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-6098" data-mp="1cgbpzg1m60rp">
              <GitBranch className="w-4 h-4 text-slate-300" data-id="element-6099" data-mp="17hie1s1z6htn" />
              Ontsluit ({nodeData.dependents.length})
            </h4>
            {nodeData.dependents.length === 0 ? <div className="text-sm text-slate-500 italic bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-6100" data-mp="es1n6yu078zj">
                Dit is een leaf node (ontsluit niets).
              </div> : <div className="space-y-2" data-id="element-6101" data-mp="onmsb5udcpdi">
                {nodeData.dependents.map(dep => <button key={dep} onClick={() => onSelectNode(dep)} className="w-full text-left p-2.5 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors group flex items-center justify-between" data-id="element-6102" data-mp="252entrmtacr1">
                
                    <span className="font-mono text-sm text-slate-700 group-hover:text-indigo-700 transition-colors truncate" data-id="element-6103" data-mp="18twia9q8fgte">
                      {dep}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-400" data-id="element-6104" data-mp="z0fqfb9nobsd" />
                  </button>)}
              </div>}
          </div>
        </div>
      </div>
    </>;
}